import React from "react";
import styles from "./Chart.module.css";

/**
 * Shared shell around any visualisation: header (icon / title / subtitle /
 * actions), the loading-error-empty states, and an optional footer hint.
 * Keeping this in one place means every chart in the app reports the same
 * way while it is fetching or has nothing to show.
 *
 * Props
 *  - title, subtitle, icon      header content
 *  - note                       highlighted "Note: …" pill in the header
 *  - actions                    extra nodes rendered before the back button
 *  - onBack, backLabel          renders the ✕ button when onBack is provided
 *  - loading                    overlays a spinner on top of the chart
 *  - error                      overlays an error message
 *  - empty, emptyText           replaces the body with a muted message
 *  - hint                       small italic caption under the chart
 *  - bodyClassName, height      body sizing (height only when not supplied by CSS)
 */
export default function ChartFrame({
  title,
  subtitle,
  icon = null,
  note = "",
  actions = null,
  onBack,
  backLabel = "Close drill-down view",
  loading = false,
  error = null,
  empty = false,
  emptyText = "No data available.",
  hint = "",
  bodyClassName = "",
  height,
  children,
}) {
  const showHeader = Boolean(title || subtitle || icon || note || actions || onBack);

  return (
    <div className={styles.frame}>
      {showHeader && (
        <div className={styles.frameHeader}>
          <div className={styles.frameHeaderLeft}>
            {icon && (
              <span className={styles.frameIcon} aria-hidden="true">
                {icon}
              </span>
            )}
            <div>
              {title && <h3 className={styles.frameTitle}>{title}</h3>}
              {subtitle && <div className={styles.frameSubtitle}>{subtitle}</div>}
            </div>
          </div>

          <div className={styles.frameHeaderRight}>
            {note && (
              <div className={styles.frameNote}>
                <strong>Note:</strong> {note}
              </div>
            )}
            {actions}
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                aria-label={backLabel}
                className={styles.frameBackBtn}
              >
                ✕
              </button>
            )}
          </div>
        </div>
      )}

      {empty && !loading && !error ? (
        <div className={styles.frameMuted}>{emptyText}</div>
      ) : (
        <div
          className={`${styles.frameBody} ${bodyClassName}`.trim()}
          style={height ? { height } : undefined}
        >
          {loading && (
            <div className={styles.frameOverlay}>
              <span className={styles.frameOverlayText}>
                <span className={styles.frameSpinner} />
                Loading…
              </span>
            </div>
          )}
          {error && !loading && (
            <div className={styles.frameOverlay}>
              <span className={styles.frameOverlayText}>{error}</span>
            </div>
          )}
          {children}
        </div>
      )}

      {hint && <div className={styles.frameHint}>{hint}</div>}
    </div>
  );
}
